import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, inject, watch } from 'vue';
import ModalBase from '@/components/ui/modals/modal-base.vue';
import { DxToolbarItem } from 'devextreme-vue/popup';
import FindContactModal from '../find/contacts/find-contact-modal.vue';
import GridBase from '../ui/grid/grid-base.vue';
import { ContactFindResult } from '@/types/find/contacts/ContactFindResult'
import { DxColumn } from "devextreme-vue/data-grid";
import { ContactsToCopyTo } from '@/types/emails/ContactsToCopyTo';
import { AcquaintApiClient } from '@/services/AcquaintApiClient';
import { Contacts } from 'acquaint-api/models/Contacts';
import displayErrorMessage from '@/utils/ErrorHandling';
import { getErrorMessage } from 'bright-logic-core/src/errors';
import displayInfoToast from '@/alerts/infoToastAlert';
import { AcquaintApiNextClient } from '@/services/AcquaintApiNextClient';
import { IdNamePair } from '@/types/generic/IdNamePair';
import GridMenu from '../ui/grid/grid-menu.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'select-cc-contacts-modal',
  props: {
    /** 
     * The boolean value used with `v-model` to control the visibility or state of a modal or component.
     * @type {boolean}
     * @required
     */
    modelValue: {
        type: Boolean,
        required: true,
    },

    /** 
     * Determines whether the content should be copied to another location or component.
     * @type {boolean}
     * @default true
     */
    copyTo: {
        type: Boolean,
        default: true,
    },

    /** 
     * Determines whether lists should be cleared or reset.
     * @type {boolean}
     * @default false
     */
    clearLists: {
        type: Boolean,
        default: false,
    },

    /** 
     * The unique identifier for the contact.
     * @type {number}
     * @default -1
     */
    contactId: {
        type: Number,
        default: -1,
    },

    /** 
     * Determines whether the action or component is intended for email-related functionality.
     * @type {boolean}
     * @default true
     */
    forEmail: {
        type: Boolean,
        default: true,
    },

    /** 
     * Indicates whether the contact is the primary contact.
     * @type {boolean}
     * @required
     */
    isPrimaryContact: {
        type: Boolean,
        required: true,
    },
},
  emits: [
    /** 
     * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
     * @property {boolean} value - Indicates whether the modal has been opened or closed.
     */
    'update:modelValue',

    /** 
     * Emitted when saving contacts with visible email addresses (CC).
     * @property {Array<string>} contactNames - The names of the contacts being saved.
     * @property {Array<string>} contactNamesEmails - The email addresses of the contacts being saved.
     * @property {Array<number>} contactIds - The unique IDs of the contacts being saved.
     */
    'saveCopyToContacts',

    /** 
     * Emitted when saving contacts without visible email addresses (BCC).
     * @property {Array<string>} contactNames - The names of the contacts being saved.
     * @property {Array<number>} contactIds - The unique IDs of the contacts being saved.
     */
    'saveBlindCopyToContacts',
],
  setup(__props, { emit }) {

const props = __props;





const apiNextClient = inject('AcquaintApiNextClient') as AcquaintApiNextClient;

const openfindContactModal = ref(false);
const ccContacts = ref<ContactsToCopyTo[]>([]);
const bccContacts = ref<ContactsToCopyTo[]>([]);
const apiClient = inject('AcquaintApiClient') as AcquaintApiClient
const fullContactInfo = ref<Contacts>();
const selectedContactId = ref(-1);
const loading = ref(false);
const fetchedLinkedContacts = ref<IdNamePair[]>([]);
const gridBaseKey = ref(0);

const showModal = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
});

const onCancelClick = () => showModal.value = false;

const addContact = () => {
    openfindContactModal.value = true;
};

const contactsToDisplay = computed(() => { return props.copyTo ? ccContacts.value : bccContacts.value });

watch(() => showModal.value, async () => {
    if (!showModal.value) {
        return;
    }
});

watch(() => props.clearLists, () => {
    if (props.clearLists) {
        bccContacts.value = [];
        ccContacts.value = [];
    }
});

const contactNames = computed(() => {
    let names = '';
    const contacts = props.copyTo ? ccContacts.value : bccContacts.value;
    contacts.forEach((contact, index) => {
        // If the contact is the first and only item or the last item, we don't want to add a comma after the name
        if (index == 0 && contacts.length == 1 || index == contacts.length - 1) {
            names = names + `${contact.firstName} ${contact.lastName}`;
        } else {
            names = names + `${contact.firstName} ${contact.lastName}, `;
        }
    });

    return names;
});

const contactNamesEmails = computed(() => {
    let names = '';
    const contacts = props.copyTo ? ccContacts.value : bccContacts.value;
    contacts.forEach((contact, index) => {
        // If the contact is the first and only item or the last item, we don't want to add a comma after the name
        if (index == 0 && contacts.length == 1 || index == contacts.length - 1) {
            names = names + `${contact.firstName} ${contact.lastName} - ${contact.email} ${contact.altEmail}`;
        } else {
            names = names + `${contact.firstName} ${contact.lastName} ${contact.email} ${contact.altEmail}, `;
        }
    });

    return names;
});

const contactIds = computed(() => {
    let ids: number[] = [];
    const contacts = props.copyTo ? ccContacts.value : bccContacts.value;
    contacts.forEach((contact) => {
        ids.push(contact.id);
    });
    return ids;
});

const onSaveClick = () => {
    if (props.copyTo) {
        emit('saveCopyToContacts', contactNames.value, contactNamesEmails.value, contactIds.value);
    } else {
        emit('saveBlindCopyToContacts', contactNames.value, contactIds.value);
    }

    showModal.value = false;
};

const fetchMainData = async (contactId: number) => {
    try {
        fullContactInfo.value = await apiClient.httpGet<Contacts>(`database/contacts/${contactId}`);
    } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
        fullContactInfo.value = undefined;
    }
};

const contactSelected = (selectedContact: ContactFindResult) => {
    if (!selectedContact) {
        return;
    }

    selectedContactId.value = selectedContact.id;
};

const deleteContact = () => {
    if (props.copyTo) {
        ccContacts.value = ccContacts.value.filter((contact) => { return contact.id != selectedContactId.value });
        selectedContactId.value = -1;
    } else {
        bccContacts.value = bccContacts.value.filter((contact) => { return contact.id != selectedContactId.value });
        selectedContactId.value = -1;
    }
};

const primaryContactSelected = (id: number) => {
    if (!id) {
        return;
    }

    addContactToList(id);
}

const addContactToList = async (id: number) => {
    loading.value = true;

    await fetchMainData(id);

    if (!fullContactInfo.value) {
        loading.value = false;
        return;
    }

    if (fullContactInfo.value.email == '' && props.forEmail) {
        loading.value = false;
        displayInfoToast('Sorry, this Contact does not have an Email address.');
        return;
    }

    if (fullContactInfo.value.mobileTelephone == '' && !props.forEmail) {
        loading.value = false;
        displayInfoToast('Sorry, this Contact does not have a Mobile Number.');
        return;
    }

    if (props.copyTo) {
        ccContacts.value.push({
            id: fullContactInfo.value.id,
            firstName: fullContactInfo.value.firstNames,
            lastName: fullContactInfo.value.lastName,
            company: fullContactInfo.value.businessCompany,
            email: fullContactInfo.value.email,
            altEmail: fullContactInfo.value.emailAlt
        });
    } else {
        bccContacts.value.push({
            id: fullContactInfo.value.id,
            firstName: fullContactInfo.value.firstNames,
            lastName: fullContactInfo.value.lastName,
            company: fullContactInfo.value.businessCompany,
            email: fullContactInfo.value.email,
            altEmail: fullContactInfo.value.emailAlt
        });
    }

    loading.value = false;
};

const contactToBeAdded = async (selectedContact: ContactFindResult) => {
    if (!selectedContact) {
        return;
    }

    addContactToList(selectedContact.id);
};

const fetchLinkedContacts = async () => {
    try {
        fetchedLinkedContacts.value = await apiNextClient.httpGet(`/contacts/${props.contactId}/links`);
    } catch (err) {
        displayErrorMessage('The linked contacts could not be fetched');
        fetchedLinkedContacts.value = [];
    }
};

const addAllLinks = async () => {
    loading.value = true;
    await fetchLinkedContacts();

    if (fetchedLinkedContacts.value.length == 0) {
        loading.value = false;
        displayErrorMessage('The linked contacts could not be fetched.')
        return;
    }

    // After fetching the linked contacts, we fetch the data of each contact and add it the table
    for (let i = 0; i < fetchedLinkedContacts.value.length; i++) {

        // We must check if the linked contact is already displayed in the table and if it is, we 
        // can't call the api to then add it's details to the table.
        let contactInTable: ContactsToCopyTo | undefined;

        if (props.copyTo) {
            contactInTable = ccContacts.value.find((contact) => { return contact.id == fetchedLinkedContacts.value[i].id });
        } else {
            contactInTable = bccContacts.value.find((contact) => { return contact.id == fetchedLinkedContacts.value[i].id });
        }

        if (contactInTable) {
            continue;
        }

        let linkedContactData: Contacts | undefined;

        linkedContactData = await fetchLinkedContactData(fetchedLinkedContacts.value[i].id);

        if (!linkedContactData) {
            continue;
        }

        if (props.forEmail && linkedContactData.email == '') {
            continue;
        } else if (!props.forEmail && linkedContactData.mobileTelephone == '') {
            continue;
        }

        if (props.copyTo) {
            ccContacts.value.push({
                id: linkedContactData?.id,
                firstName: linkedContactData.firstNames,
                lastName: linkedContactData.lastName,
                company: linkedContactData.businessCompany,
                email: linkedContactData?.email,
                altEmail: linkedContactData?.emailAlt
            });
        } else {
            bccContacts.value.push({
                id: linkedContactData?.id,
                firstName: linkedContactData.firstNames,
                lastName: linkedContactData.lastName,
                company: linkedContactData.businessCompany,
                email: linkedContactData?.email,
                altEmail: linkedContactData?.emailAlt
            });
        }
    }
    loading.value = false;
    gridBaseKey.value++;
};

const fetchLinkedContactData = async (contactId: number) => {
    try {
        return await apiClient.httpGet<Contacts>(`database/contacts/${contactId}`);
    } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
        return undefined;
    }
};

const dropdownMenu = computed(() => {
    const menuItems = [];

    if (props.isPrimaryContact) {
        menuItems.push({
            text: 'Add All Links',
            key: 'addAllLinks',
            icon: 'fas fa-users'
        });
    }

    menuItems.push(
        {
            text: 'Add',
            key: 'addContact',
            icon: 'fas fa-plus'
        },
        {
            text: 'Delete',
            key: 'deleteContact',
            icon: 'fas fa-trash'
        }
    );

    return menuItems;
});

const onMenuItemClick = (key: string) => {
    switch (key) {
        case 'addContact':
            addContact();
            break;
        case 'addAllLinks':
            addAllLinks();
            break;
        case 'deleteContact':
            deleteContact();
            break;
    }
};

const modalTitle = computed(() => { return props.forEmail ? "Contacts to Copy Email To" : "Contacts to Copy SMS To" });

const menuId = computed(() => {
    const method = props.forEmail ? 'email' : 'sms';
    const type = props.copyTo ? 'cc' : 'bcc';

    return `${method}-${type}-contacts-btn`;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
      title: modalTitle.value,
      height: "360px"
    }, {
      content: _withCtx(() => [
        (_openBlock(), _createBlock(GridBase, {
          key: gridBaseKey.value,
          loading: loading.value,
          dataSource: contactsToDisplay.value,
          onRowSelected: contactSelected,
          "id-column-name": "id",
          showColumnHeaders: true
        }, {
          "header-content": _withCtx(() => [
            _createVNode(GridMenu, {
              "menu-items": dropdownMenu.value,
              onItemClicked: onMenuItemClick,
              class: "grid-menu-button",
              id: menuId.value
            }, null, 8, ["menu-items", "id"])
          ]),
          columns: _withCtx(() => [
            _createVNode(_unref(DxColumn), {
              "data-field": "lastName",
              caption: "Last Name",
              "hiding-priority": 8
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "firstName",
              caption: "First Name",
              "hiding-priority": 8
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "company",
              caption: "Company",
              "hiding-priority": 8
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "email",
              caption: "Email",
              "hiding-priority": 8,
              visible: false
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "altEmail",
              caption: "AltEmail",
              "hiding-priority": 8,
              visible: false
            })
          ]),
          _: 1
        }, 8, ["loading", "dataSource"]))
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Save', onClick: () => { onSaveClick() } }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Cancel', onClick: () => { onCancelClick() } }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(FindContactModal, {
      modelValue: openfindContactModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((openfindContactModal).value = $event)),
      onContactSelected: contactToBeAdded,
      onPrimaryContactSelected: primaryContactSelected,
      "current-contact-id": props.contactId
    }, null, 8, ["modelValue", "current-contact-id"])
  ], 64))
}
}

})